import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../css/chart-main.css";
import "../../css/charts-overview.css";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { useRecentViews, useSectors, useTopSectors } from "../../hooks";
import { interactionService } from "../../services";
import { translateSectorLabel } from "../../utils/sectorLabel";

const ChartsOverview = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;

  // Fetch recently viewed materials from API (requires authentication)
  const { data: materials, loading, error } = useRecentViews({
    limit: 8,
    enabled: true,
    cache: true,
    cacheTTL: 2 * 60 * 1000, // 2 minutes
  });

  // Fetch all sectors from API
  const { data: sectors, loading: sectorsLoading, error: sectorsError } = useSectors({
    enabled: true,
    cache: true,
    cacheTTL: 30 * 60 * 1000, // 30 minutes
  });

  // Fetch top personalized sectors for the user (Related Fields) (requires authentication)
  const { data: topSectors, loading: topSectorsLoading, error: topSectorsError } = useTopSectors({
    limit: 4,
    enabled: true,
    cache: true,
    cacheTTL: 10 * 60 * 1000, // 10 minutes
  });

  // Check if errors are authentication-related
  const isAuthError = (err) => {
    return err && (err.message?.includes('401') || err.message?.includes('Unauthorized'));
  };

  const isServerError = (err) => {
    return err && (err.message?.includes('502') || err.message?.includes('Bad Gateway'));
  };

  const recentViewsAuthError = isAuthError(error);
  const topSectorsAuthError = isAuthError(topSectorsError);
  const sectorsServerError = isServerError(sectorsError);

  // Debug: Log recently viewed materials to console
  React.useEffect(() => {
    if (materials) {
      console.log('Recently viewed materials loaded:', materials);
      console.log('First material:', materials[0]);
    }
  }, [materials]);

  const handleMaterialClick = (material) => {
    // Only navigate if material has a code and is not disabled
    if (material.code && !material.disabled) {
      // Track material view (fire-and-forget, don't wait for response)
      interactionService.trackMaterialView(material.id);

      // Navigate to material detail page
      navigate(`/charts/material/${material.code}`);
    }
  };

  const handleSectorClick = (sectorId, sectorName) => {
    navigate(`/charts/sector/${sectorId}`, { state: { sectorName } });
  };



  // Simple inline chart using SVG - full width with gradient
  const renderMiniChart = (data, uniqueId) => {
    // Safety check for empty or invalid data
    if (!data || data.length === 0) {
      return null;
    }

    const height = 120;
    const paddingTop = 10;
    const paddingBottom = 10;
    const paddingLeft = 0;
    const paddingRight = 0;

    const chartWidth = 100 - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    // Generate line points
    const linePoints = data
      .map((value, index) => {
        const x = paddingLeft + (index / (data.length - 1)) * chartWidth;
        const y = paddingTop + (chartHeight - ((value - min) / range) * chartHeight);
        return `${x},${y}`;
      })
      .join(" ");

    // Generate filled area points (includes bottom corners)
    const areaPoints = `${paddingLeft},${height - paddingBottom} ${linePoints} ${chartWidth},${height - paddingBottom}`;

    return (
      <svg
        width="100%"
        height={height}
        className="material-chart"
        preserveAspectRatio="none"
        viewBox={`0 0 100 ${height}`}
      >
        <defs>
          <linearGradient id={`gradient-${uniqueId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style={{ stopColor: "#8B5CF6", stopOpacity: 0.5 }} />
            <stop offset="100%" style={{ stopColor: "#8B5CF6", stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {/* Gradient fill area */}
        <polygon
          fill={`url(#gradient-${uniqueId})`}
          points={areaPoints}
        />
        {/* Line on top */}
        <polyline
          fill="none"
          stroke="#8B5CF6"
          strokeWidth="0.5"
          points={linePoints}
        />
      </svg>
    );
  };

  return (
    <div className="charts-overview">

      {/* Recent Views Section */}
      <section className="overview-section">
        <h2 className="section-title">{t("charts.overview.recentViews")}</h2>

        {loading && (
          <div className="loading-state">
            <p>{t("charts.overview.loadingMaterials")}</p>
          </div>
        )}

        {recentViewsAuthError && (
          <div className="error-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>{t("charts.overview.loginRecentViews")}</p>
          </div>
        )}

        {error && !recentViewsAuthError && (
          <div className="error-state">
            <p>{t("charts.overview.errorLoadingMaterials", { message: error.message })}</p>
          </div>
        )}

        {!loading && !error && materials && materials.length > 0 && (
          <div className="materials-grid ">
            {materials.map((material) => (
              <div
                key={material.id}
                className={`material-card ${material.disabled ? 'disabled' : ''}`}
                onClick={() => handleMaterialClick(material)}
                style={{
                  opacity: material.disabled ? 0.5 : 1,
                  cursor: material.disabled ? 'not-allowed' : 'pointer'
                }}
              >
                {/* Price and Date - Top Right */}
                <div className="material-header">
                  <div className="material-price">{material.price || t("common.na")}</div>
                  <div className="material-date">{material.date || t("common.na")}</div>
                </div>

                {/* Chart - Center, Full Width */}
                <div className="material-chart-wrapper">
                  {renderMiniChart(material.chartData, material.id)}
                </div>

                {/* Material Name - Bottom Left */}
                <div className="material-footer">
                  <div className="material-name">{material.name}</div>
                  {material.disabled && (
                    <div className="material-subtitle" style={{ color: '#ef4444' }}>
                      {t("charts.overview.incompleteData")}
                    </div>
                  )}
                  {!material.disabled && material.description && (
                    <div className="material-subtitle">{material.description}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Related Fields Section - Personalized top sectors for the user */}
      <section className="overview-section">
        <h2 className="section-title">{t("charts.overview.relatedFields")}</h2>

        {topSectorsLoading && (
          <div className="loading-state">
            <p>{t("charts.overview.loadingPersonalizedSectors")}</p>
          </div>
        )}

        {topSectorsAuthError && (
          <div className="error-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>{t("charts.overview.loginPersonalizedSectors")}</p>
          </div>
        )}

        {topSectorsError && !topSectorsAuthError && (
          <div className="error-state">
            <p>{t("charts.overview.errorLoadingPersonalizedSectors", { message: topSectorsError.message })}</p>
          </div>
        )}

        {!topSectorsLoading && !topSectorsError && topSectors && (
          <div className="sectors-grid">
            {topSectors.map((sector) => (
              <div
                key={sector.id}
                className="sector-card"
                onClick={() => handleSectorClick(sector.id, sector.name)}
              >
                <div className="sector-image">
                  <img
                    src={sector.image || placeholderImage}
                    alt={translateSectorLabel(sector.name, lang)}
                    onError={(e) => { e.target.src = placeholderImage; }}
                  />
                  <div className="sector-name-overlay">{translateSectorLabel(sector.name, lang)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* All Sectors Section */}
      <section className="overview-section">
        <h2 className="section-title">{t("charts.overview.allSectors")}</h2>

        {sectorsLoading && (
          <div className="loading-state">
            <p>{t("charts.overview.loadingSectors")}</p>
          </div>
        )}

        {sectorsServerError && (
          <div className="error-state" style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>{t("charts.overview.sectorsTemporarilyUnavailable")}</p>
          </div>
        )}

        {sectorsError && !sectorsServerError && (
          <div className="error-state">
            <p>{t("charts.overview.errorLoadingSectors", { message: sectorsError.message })}</p>
          </div>
        )}

        {!sectorsLoading && !sectorsError && sectors && (
          <div className="sectors-grid">
            {sectors.map((sector) => (
              <div
                key={`all-${sector.id}`}
                className="sector-card"
                onClick={() => handleSectorClick(sector.id, sector.name)}
              >
                <div className="sector-image">
                  <img
                    src={sector.image || placeholderImage}
                    alt={translateSectorLabel(sector.name, lang)}
                    onError={(e) => { e.target.src = placeholderImage; }}
                  />
                  <div className="sector-name-overlay">{translateSectorLabel(sector.name, lang)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ChartsOverview;
